import { useMemo } from 'react';
import { isArray } from 'lodash';
import excludeSvg from 'assets/icons/exclude.svg';
import cx from 'classnames';
import Chip from 'lib/Chip';
import { Color, TextPriority } from 'lib/Chip/Chip.types';
import { ListProps, SectionButtonChipListProps } from './SectionButtonChipList.types';
import ChipWithTooltip from './ChipWithTooltip';

const ExpandedList: React.FC<ListProps> = ({
  lineId,
  category,
  chips,
  isExcluded,
  hasIndicator = false,
  chipColor = Color.PRIMARY,
  chipTextPriority = TextPriority.LABEL,
  truncateChipText = true,
  onRemove,
  isDisabled,
}) => {
  const tooltipContent = chips
    .map((chip) => (chip.name ? `${category}: ${chip.name}` : category))
    .filter((value, index, self) => self.indexOf(value) === index);

  return (
    <ChipWithTooltip tooltipContent={tooltipContent}>
      <Chip
        key={`${lineId}-${category}-${chips[0].name}-chip`}
        label={category}
        subLabel={chips[0].name}
        color={isExcluded ? Color.GREY : chipColor}
        textPriority={chipTextPriority}
        icon={isExcluded ? excludeSvg : null}
        hasIndicator={hasIndicator}
        truncateLabel={truncateChipText}
        isDisabled={isDisabled}
        onRemove={onRemove ? () => onRemove([chips[0].name, category], !isExcluded) : undefined}
      />
      {chips.slice(1).map((chip) => (
        <Chip
          key={`${lineId}-${category}-${chip.name}-chip`}
          subLabel={chip.name}
          color={isExcluded ? Color.GREY : chipColor}
          textPriority={chipTextPriority}
          hasIndicator={hasIndicator}
          truncateLabel={truncateChipText}
          onRemove={onRemove ? () => onRemove([chip.name, category], !isExcluded) : undefined}
          isDisabled={isDisabled}
        />
      ))}
    </ChipWithTooltip>
  );
};

const CollapsedList: React.FC<ListProps> = ({
  lineId,
  category,
  chips,
  isExcluded,
  truncateChipText,
  onRemove,
  isDisabled,
  chipColor,
}) => {
  const tooltipContent = chips.map((chip) => chip.name);

  return (
    <ChipWithTooltip tooltipContent={tooltipContent}>
      <Chip
        key={`${lineId}-${category}-collapsed-list`}
        icon={isExcluded ? excludeSvg : null}
        label={category}
        subLabel={chips.length.toString()}
        color={chipColor}
        truncateLabel={truncateChipText}
        onRemove={onRemove ? () => onRemove([category, ...chips.map((chip) => chip.name)], !isExcluded) : undefined}
        isDisabled={isDisabled}
      />
    </ChipWithTooltip>
  );
};

const SectionButtonChipList: React.FC<SectionButtonChipListProps> = ({
  lineId,
  selected,
  maxSelected = 5,
  hasIndicator = false,
  chipColor = Color.PRIMARY,
  chipTextPriority = TextPriority.LABEL,
  chipAlign = 'items-end',
  truncateChipText = true,
  onRemove,
  isDisabled = false,
}) => {
  // eslint-disable-next-line sonarjs/cognitive-complexity
  const content = useMemo(() => {
    if (isArray(selected))
      return (
        <ChipWithTooltip tooltipContent={selected}>
          {selected.map((value) => (
            <Chip
              key={`${lineId}-${value}-selected-chip`}
              label={value}
              color={chipColor}
              textPriority={chipTextPriority}
              hasIndicator={hasIndicator}
              truncateLabel={truncateChipText}
              isDisabled={isDisabled}
              onRemove={onRemove ? () => onRemove([value]) : undefined}
            />
          ))}
        </ChipWithTooltip>
      );

    return Object.keys(selected).map((category) => {
      const chips = selected[category];

      if (isArray(chips)) {
        if (chips.length <= maxSelected)
          return (
            <ExpandedList
              key={`${lineId}-${category}-expanded-list`}
              category={category}
              chips={chips}
              hasIndicator={hasIndicator}
              chipColor={chipColor === Color.DISABLED ? chipColor : (chips[0].color ?? chipColor)}
              chipTextPriority={chipTextPriority}
              truncateChipText={truncateChipText}
              isDisabled={isDisabled}
              onRemove={onRemove ? () => onRemove([category]) : undefined}
            />
          );

        return (
          <CollapsedList
            key={`${lineId}-${category}-collapsed-list`}
            category={category}
            chips={chips}
            truncateChipText={truncateChipText}
            onRemove={onRemove ? () => onRemove([category]) : undefined}
            isDisabled={isDisabled}
            chipColor={chipColor}
          />
        );
      }

      const { included, excluded } = chips;
      const includedExcludedList: JSX.Element[] = [];

      if (included) {
        if (included.length <= maxSelected) {
          includedExcludedList.push(
            <ExpandedList
              key={`${lineId}-${category}-expanded-included-list`}
              category={category}
              chips={included}
              chipTextPriority={chipTextPriority}
              truncateChipText={truncateChipText}
              onRemove={onRemove}
              isDisabled={isDisabled}
              chipColor={chipColor}
            />,
          );
        } else
          includedExcludedList.push(
            <CollapsedList
              key={`${lineId}-${category}-collapsed-included-list`}
              category={category}
              chips={included}
              truncateChipText={truncateChipText}
              onRemove={onRemove}
              isDisabled={isDisabled}
              chipColor={chipColor}
            />,
          );
      }

      if (excluded) {
        if (excluded.length <= maxSelected) {
          includedExcludedList.push(
            <ExpandedList
              key={`${lineId}-${category}-expanded-excluded-list`}
              category={category}
              chips={excluded}
              chipTextPriority={chipTextPriority}
              isExcluded
              truncateChipText={truncateChipText}
              onRemove={onRemove}
              isDisabled={isDisabled}
              chipColor={chipColor}
            />,
          );
        } else
          includedExcludedList.push(
            <CollapsedList
              key={`${lineId}-${category}-collapsed-excluded-list`}
              category={category}
              chips={excluded}
              isExcluded
              truncateChipText={truncateChipText}
              onRemove={onRemove}
              isDisabled={isDisabled}
              chipColor={chipColor}
            />,
          );
      }

      return includedExcludedList;
    });
  }, [selected, isDisabled, chipColor]);

  return <div className={cx(`flex w-full flex-col align-middle gap-1`, chipAlign)}>{content}</div>;
};

export default SectionButtonChipList;
